import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { FeatureFlagsProvider } from './hooks/useFeatureFlags';
import Layout from './components/Layout';
import Home from './pages/Home';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import PhotoSelection from './pages/PhotoSelection';
import Payment from './pages/Payment';
import JobResults from './pages/JobResults';
import JobHistory from './pages/JobHistory';
import Agent from './pages/Agent';
import Settings from './pages/Settings';
import Account from './pages/Account';
import ChangePassword from './pages/ChangePassword';
import AuthSuccess from './pages/AuthSuccess';
import AuthError from './pages/AuthError';
import ManualAuth from './pages/ManualAuth';
import VerifyEmail from './pages/VerifyEmail';
import LoadingSpinner from './components/LoadingSpinner';
import SetPassword from './pages/SetPassword';

function App() {
  return (
    <FeatureFlagsProvider>
      <AppContent />
    </FeatureFlagsProvider>
  );
}

function AppContent() {
  const { user, loading, checkAuth } = useAuthStore();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initializeApp = async () => {
      console.log('🚀 App: Initializing app...');
      console.log('🚀 App: Environment variables:', {
        REACT_APP_NAME: process.env.REACT_APP_NAME,
        REACT_APP_API_URL: process.env.REACT_APP_API_URL,
        NODE_ENV: process.env.NODE_ENV
      });
      await checkAuth();
      setIsInitialized(true);
      console.log('✅ App: App initialized');
    };
    
    initializeApp();
  }, [checkAuth]);

  if (!isInitialized || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    console.log('🔒 App: No user found, showing public routes');
    return (
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/auth-success" element={<AuthSuccess />} />
        <Route path="/auth-error" element={<AuthError />} />
        <Route path="/manual-auth" element={<ManualAuth />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Handle temporary users (email verified but no provider connected)
  if (user.isTemporary) {
    console.log('📧 App: Email verified user, showing limited authenticated routes');
    return (
      <Layout>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/account" element={<Account />} />
          <Route path="/settings/password" element={<SetPassword />} />
          <Route path="/account/change-password" element={<ChangePassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/auth-success" element={<AuthSuccess />} />
          <Route path="/auth-error" element={<AuthError />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/photos" element={<PhotoSelection />} />
        <Route path="/payment/:jobId" element={<Payment />} />
        <Route path="/job/:jobId" element={<JobResults />} />
        <Route path="/history" element={<JobHistory />} />
        <Route path="/agent" element={<Agent />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/account" element={<Account />} />
        <Route path="/settings/password" element={<SetPassword />} />
        <Route path="/account/change-password" element={<ChangePassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default App;
